import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, lineNumbers, highlightActiveLine, keymap, drawSelection, highlightActiveLineGutter, hoverTooltip } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab, undo, redo, isolateHistory } from '@codemirror/commands';
import { bracketMatching, indentOnInput, foldGutter, foldKeymap } from '@codemirror/language';
import { autocompletion, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { setDiagnostics, type Diagnostic } from '@codemirror/lint';
import { scadLanguage } from './scadLanguage';
import { scadDarkTheme, scadHighlighting, scadLightTheme, scadLightHighlighting } from './theme';
import { scadCompletionSource, scadHoverTooltip, signatureHelp } from './scadCompletions';
import { symbolsField, setSymbolsEffect, goToDefinitionExtension } from './symbolState';
import type { SymbolTable } from './symbols';
import type { ScadError } from '../scad/errors';
import type { ThemeMode } from '../app/theme';

export interface EditorInstance {
  view: EditorView;
  setCode(code: string): void;
  replaceRange(from: number, to: number, text: string): void;
  getCode(): string;
  setErrors(errors: ScadError[]): void;
  /** Update user-defined symbols (completion, hover, signature help, F12). */
  setSymbols(table: SymbolTable): void;
  goToLine(line: number, col: number): void;
  highlightRange(from: number, to: number): void;
  setTheme(mode: ThemeMode): void;
  onCursorChange(cb: (offset: number) => void): void;
  undo(): boolean;
  redo(): boolean;
  destroy(): void;
}

export function createEditor(
  container: HTMLElement,
  initialCode: string,
  onChange: (code: string) => void,
  initialTheme: ThemeMode = 'dark',
): EditorInstance {
  const cursorCbs: ((offset: number) => void)[] = [];
  let lastCursorOffset = -1;
  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      onChange(update.state.doc.toString());
    }
    if (cursorCbs.length && (update.selectionSet || update.docChanged)) {
      const head = update.state.selection.main.head;
      if (head !== lastCursorOffset) {
        lastCursorOffset = head;
        for (const cb of cursorCbs) cb(head);
      }
    }
  });

  const themeCompartment = new Compartment();
  const highlightCompartment = new Compartment();

  // Force plain-text paste: when clipboard has HTML (e.g. from web pages with
  // table markup), extract text/plain to avoid injecting <tr>/<td> tags.
  const plainTextPaste = EditorView.domEventHandlers({
    paste(event, view) {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return false;
      const plain = clipboardData.getData('text/plain');
      if (plain) {
        // Let CodeMirror handle the plain text normally by replacing selection
        const { from, to } = view.state.selection.main;
        view.dispatch({ changes: { from, to, insert: plain } });
        event.preventDefault();
        return true;
      }
      return false;
    },
  });

  const buildExtensions = (themeMode: ThemeMode) => [
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightActiveLine(),
    drawSelection(),
    indentOnInput(),
    bracketMatching(),
    closeBrackets(),
    foldGutter(),
    history(),
    autocompletion({ override: [scadCompletionSource] }),
    hoverTooltip(scadHoverTooltip),
    symbolsField,
    signatureHelp,
    goToDefinitionExtension,
    keymap.of([
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...historyKeymap,
      ...foldKeymap,
      indentWithTab,
    ]),
    scadLanguage,
    themeCompartment.of(themeMode === 'light' ? scadLightTheme : scadDarkTheme),
    highlightCompartment.of(themeMode === 'light' ? scadLightHighlighting : scadHighlighting),
    updateListener,
    EditorView.lineWrapping,
    plainTextPaste,
  ];

  let currentTheme: ThemeMode = initialTheme;
  const state = EditorState.create({
    doc: initialCode,
    extensions: buildExtensions(initialTheme),
  });

  const view = new EditorView({ state, parent: container });

  // Replace the document wholesale AND wipe the undo history. Using a plain
  // dispatch({changes:...}) would record the replacement as a single large
  // undo step, so after enough undos the user would roll back past the load
  // and end up looking at whatever document was in the editor before the
  // project-tab switch. Re-creating the EditorState side-steps that entirely.
  function setCode(code: string): void {
    const newState = EditorState.create({
      doc: code,
      extensions: buildExtensions(currentTheme),
    });
    view.setState(newState);
    // setState bypasses the updateListener, so notify onChange manually to
    // keep the rest of the app (dirty flag, re-evaluation, etc.) in sync.
    onChange(code);
    lastCursorOffset = -1;
  }

  // Incremental edit — goes through view.dispatch so CodeMirror records a
  // normal undoable change. Use this for every programmatic edit that
  // should be reversible (sketch-editor rewrites, AI snippet applies,
  // DimensionOverlay commits). Full-document loads (tab switches,
  // examples, share URL) must continue to use setCode() to wipe history.
  function replaceRange(from: number, to: number, text: string): void {
    const len = view.state.doc.length;
    const fromClamped = Math.max(0, Math.min(from, len));
    const toClamped = Math.max(fromClamped, Math.min(to, len));
    view.dispatch({
      changes: { from: fromClamped, to: toClamped, insert: text },
      // Each programmatic edit is its own undo step — never merged with a
      // previous edit that happened within the history's grouping delay.
      annotations: isolateHistory.of('full'),
    });
  }

  function getCode(): string {
    return view.state.doc.toString();
  }

  function setErrors(errors: ScadError[]): void {
    const doc = view.state.doc;
    const diagnostics: Diagnostic[] = [];

    for (const err of errors) {
      // Convert line/col to absolute offset
      let from = 0;
      let to = 0;
      try {
        const lineFrom = Math.min(err.from.line, doc.lines);
        const lineTo = Math.min(err.to.line, doc.lines);
        const lineFromInfo = doc.line(lineFrom);
        const lineToInfo = doc.line(lineTo);
        from = lineFromInfo.from + Math.min(err.from.col - 1, lineFromInfo.to - lineFromInfo.from);
        to = lineToInfo.from + Math.min(err.to.col - 1, lineToInfo.to - lineToInfo.from);
      } catch {
        // If line/col out of bounds, put at end
        from = to = doc.length;
      }

      // Ensure from < to for visibility
      if (from === to) {
        to = Math.min(from + 1, doc.length);
      }

      const fix = err.suggestion;
      diagnostics.push({
        from,
        to,
        severity: err.severity,
        message: err.message,
        actions: fix ? [{
          name: `Replace with '${fix.replacement}'`,
          apply(v, dFrom, dTo) {
            // Replace the misspelled identifier inside the diagnostic range
            const text = v.state.doc.sliceString(dFrom, dTo);
            const at = text.search(new RegExp(`\\b${fix.word.replace(/[$]/g, '\\$&')}\\b`));
            if (at < 0) return;
            v.dispatch({ changes: { from: dFrom + at, to: dFrom + at + fix.word.length, insert: fix.replacement } });
          },
        }] : undefined,
      });
    }

    view.dispatch(setDiagnostics(view.state, diagnostics));
  }

  function setSymbols(table: SymbolTable): void {
    view.dispatch({ effects: setSymbolsEffect.of(table) });
  }

  function goToLine(line: number, col: number): void {
    const doc = view.state.doc;
    const lineNum = Math.max(1, Math.min(line, doc.lines));
    const lineInfo = doc.line(lineNum);
    const pos = lineInfo.from + Math.max(0, Math.min(col - 1, lineInfo.to - lineInfo.from));
    view.dispatch({
      selection: { anchor: pos },
      scrollIntoView: true,
    });
    view.focus();
  }

  function highlightRange(from: number, _to: number): void {
    // Move caret to the start of the range without painting a multi-line
    // selection — the selection overlay covers large code regions in amber
    // and makes highlighted modules hard to read.
    const clamped = Math.min(from, view.state.doc.length);
    view.dispatch({
      selection: { anchor: clamped },
      scrollIntoView: true,
    });
  }

  function setTheme(mode: ThemeMode): void {
    currentTheme = mode;
    view.dispatch({
      effects: [
        themeCompartment.reconfigure(mode === 'light' ? scadLightTheme : scadDarkTheme),
        highlightCompartment.reconfigure(mode === 'light' ? scadLightHighlighting : scadHighlighting),
      ],
    });
  }

  function onCursorChange(cb: (offset: number) => void): void {
    cursorCbs.push(cb);
  }

  function destroy(): void {
    view.destroy();
  }

  function undoCmd(): boolean { return undo(view); }
  function redoCmd(): boolean { return redo(view); }

  return { view, setCode, replaceRange, getCode, setErrors, setSymbols, goToLine, highlightRange, setTheme, onCursorChange, undo: undoCmd, redo: redoCmd, destroy };
}
